import { Popover } from "@base-ui/react/popover";
import { Link } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { HELP_TOPICS, type HelpId } from "~/features/help/registry";
import { cn } from "~/lib/cn";
import { useT } from "~/lib/i18n";

/**
 * The small ⓘ beside an action whose label does not tell everything: what really happens, what
 * goes out of the ERP, whether it can be undone, in a few lines, with a link to the full chapter
 * of the guide. Hover or focus opens it on a computer, a tap on a phone.
 */
export function HelpTip({ id, className }: { id: HelpId; className?: string }) {
  const t = useT();
  const topic = HELP_TOPICS[id];
  return (
    <Popover.Root>
      <Popover.Trigger
        openOnHover
        delay={250}
        aria-label={t("help.ui.about")}
        className={cn(
          "inline-flex size-6 shrink-0 items-center justify-center rounded-full text-fg-3 transition-colors outline-none hover:text-fg-2 focus-visible:ring-2 focus-visible:ring-gold/60 max-md:size-8",
          className,
        )}
      >
        <Info className="size-3.5" aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={6} className="z-50 outline-none">
          <Popover.Popup className="w-[min(320px,calc(100vw-2rem))] origin-[var(--transform-origin)] rounded-[10px] border border-line bg-surface p-3.5 text-sm text-fg-2 shadow-pop outline-none transition-[scale,opacity] duration-100 ease-out data-ending-style:scale-[0.98] data-ending-style:opacity-0 data-starting-style:scale-[0.98] data-starting-style:opacity-0">
            <p className="leading-relaxed">{t(`help.${id}`)}</p>
            <Link
              to="/help/$chapter"
              params={{ chapter: topic.chapter }}
              hash={topic.anchor}
              className="mt-2 inline-block text-xs font-medium text-fg underline-offset-4 hover:underline"
            >
              {t("help.ui.more")}
            </Link>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
